import type { JSX, ReactNode } from 'react';
import { Mail } from 'lucide-react';
import { InstagramIcon } from '@components/icons/InstagramIcon';
import { WhatsAppIcon } from '@components/icons/WhatsAppIcon';
import { Container } from '@components/ui/Container';
import { landingCopy } from '@features/landing/data/landing.pt';
import type { FooterContactItem } from '@features/landing/data/types';
import { trackWhatsAppClick } from '@lib/analytics';
import { whatsappLink } from '@lib/whatsappLink';

const FOOTER_SOURCE_ID = 'footer-cta';

const ICON_BY_KEY: Record<FooterContactItem['iconKey'], ReactNode> = {
  whatsapp: <WhatsAppIcon className="h-4 w-4" />,
  email: <Mail className="h-4 w-4" aria-hidden="true" />,
  instagram: <InstagramIcon className="h-4 w-4" />,
};

/**
 * Footer da landing (LAYOUT-04 — PRD §4.10).
 *
 * Layout 3 colunas em desktop, stack vertical em mobile:
 *  1. Marca: OmannaLogo (variant `full`) + tagline + descricao
 *  2. Contato: WhatsApp / e-mail / Instagram (cada um com icone inline)
 *  3. Navegacao: anchors internos (Servicos, Como funciona, Sobre, FAQ)
 *
 * Linha inferior: copyright separado por divisor neutral-200/30.
 *
 * Background: `bg-neutral-900` (cinza-quase-preto) com texto neutral-300.
 * Por que nao brand-900 (mesmo verde dos Diferenciais): footer e
 * institucional, nao chama-acao — neutral cria pausa visual definitiva e
 * separa do CTA Final brand-700 acima sem competir por atencao.
 *
 * WhatsApp link no footer usa `sourceId='footer-cta'` (extensao do CONV-03
 * documentado — adiciona uma 5a origem distinta ao hero/navbar/floating/final).
 *
 * Acessibilidade:
 *  - `<footer>` landmark
 *  - `<nav aria-label="Navegação do rodapé">` para a coluna de navegacao
 *  - `<address>` envolve os contatos (semantica HTML5)
 *  - Cada link tem icone aria-hidden + label visivel
 *  - Contraste: neutral-300 em neutral-900 ≈ 9.5:1 (AAA)
 */
export const Footer = (): JSX.Element => {
  const { footer } = landingCopy;
  const whatsappHref = whatsappLink(landingCopy.whatsapp.defaultMessage, FOOTER_SOURCE_ID);

  const resolveContactHref = (item: FooterContactItem): string =>
    item.iconKey === 'whatsapp' ? whatsappHref : item.href;

  return (
    <footer className="bg-neutral-900 text-neutral-300">
      <Container className="py-14 lg:py-16">
        <div className="grid gap-10 lg:grid-cols-3 lg:gap-12">
          <div>
            <span className="inline-flex items-center gap-2.5">
              <img
                src="/omanna_icon.svg"
                alt=""
                aria-hidden="true"
                decoding="async"
                loading="lazy"
                className="block h-11 w-11 shrink-0 brightness-0 invert"
              />
              <span className="inline-flex flex-col leading-none">
                <span className="font-display text-2xl font-bold tracking-tight text-white">
                  omanna
                </span>
                <span className="mt-0.5 font-sans text-[0.6rem] font-medium uppercase tracking-[0.18em] text-brand-300">
                  Provisão em Software
                </span>
              </span>
            </span>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-neutral-400">
              {footer.description}
            </p>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-brand-300">
              {footer.contactHeading}
            </h3>
            <address className="mt-5 flex flex-col gap-3 not-italic">
              {footer.contactItems.map((item) => (
                <a
                  key={item.iconKey}
                  href={resolveContactHref(item)}
                  target={(item.external ?? true) ? '_blank' : undefined}
                  rel={(item.external ?? true) ? 'noopener noreferrer' : undefined}
                  onClick={
                    item.iconKey === 'whatsapp'
                      ? () => {
                          trackWhatsAppClick(FOOTER_SOURCE_ID);
                        }
                      : undefined
                  }
                  className="inline-flex items-center gap-3 self-start text-sm text-neutral-300 outline-none transition-colors hover:text-white focus-visible:rounded-md focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-900"
                >
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-neutral-800 text-brand-300 transition-colors group-hover:bg-brand-500 group-hover:text-white">
                    {ICON_BY_KEY[item.iconKey]}
                  </span>
                  {item.label}
                </a>
              ))}
            </address>
          </div>

          <nav aria-label="Navegação do rodapé">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-brand-300">
              {footer.navHeading}
            </h3>
            <ul className="mt-5 flex flex-col gap-2.5">
              {footer.navLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-sm text-neutral-300 outline-none transition-colors hover:text-white focus-visible:rounded-md focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-900"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="mt-12 border-t border-neutral-800 pt-6 text-center text-xs text-neutral-500 sm:text-left">
          {footer.copyright}
        </div>
      </Container>
    </footer>
  );
};
